import FAB from '../../UI/FAB';
import React, { useState, useEffect } from 'react';
import { Typography, message, Space, Tag, Button } from 'antd';
import { ShoppingCartOutlined, HeartOutlined } from '@ant-design/icons';
import MarketplaceGrid from '../../UI/MarketplaceGrid';
import MarketplaceMenuCard from '../../UI/MarketplaceMenuCard';
import FoodCard from '../../UI/FoodCard';
import api from '../../../services/api';

const { Title } = Typography;

const MenuListing = ({ 
  vendorId = null,
  categoryId = null,
  showVendor = false,
  title = "Menu Items",
  showAddToCart = true,
  showFavorite = true
}) => {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState('name');

  const itemsPerPage = 12;

  useEffect(() => {
    loadMenus();
  }, [vendorId, categoryId, currentPage, filters, sortBy]);

  const loadMenus = async () => {
    try {
      setLoading(true);
      
      let endpoint = '/menus';
      const params = {
        page: currentPage,
        per_page: itemsPerPage,
        sort_by: sortBy,
        ...filters
      };

      if (vendorId) {
        endpoint = `/vendors/${vendorId}/menus`;
      }

      if (categoryId) {
        params.category_id = categoryId;
      }

      const response = await api.get(endpoint, { params });
      
      if (response.data.success) {
        setMenus(response.data.data.data || response.data.data);
        setTotalItems(response.data.data.total || response.data.data.length);
      }
    } catch (error) {
      console.error('Error loading menus:', error);
      message.error('Failed to load menu items');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (menu) => {
    // Implement add to cart logic
    message.success(`${menu.name} added to cart`);
  };

  const handleToggleFavorite = (menu) => {
    const newFavorites = new Set(favorites);
    if (favorites.has(menu.id)) {
      newFavorites.delete(menu.id);
      message.info(`${menu.name} removed from favorites`);
    } else {
      newFavorites.add(menu.id);
      message.success(`${menu.name} added to favorites`);
    }
    setFavorites(newFavorites);
  };

  const handleViewDetails = (menu) => {
    // Navigate to menu detail page
    window.location.href = `/menu/${menu.id}`;
  };

  const handleSearch = (searchTerm) => {
    setFilters(prev => ({ ...prev, search: searchTerm }));
    setCurrentPage(1);
  };

  const handleFilter = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleSort = (sortValue) => {
    setSortBy(sortValue);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const filterOptions = [
    {
      key: 'category_id',
      placeholder: 'Category',
      options: [
        { value: '1', label: 'North Indian' },
        { value: '2', label: 'South Indian' },
        { value: '3', label: 'Chinese' },
        { value: '4', label: 'Continental' },
        { value: '5', label: 'Italian' }
      ]
    },
    {
      key: 'dietary',
      placeholder: 'Dietary',
      options: [
        { value: 'vegetarian', label: 'Vegetarian' },
        { value: 'vegan', label: 'Vegan' },
        { value: 'gluten_free', label: 'Gluten Free' }
      ]
    },
    {
      key: 'price_range',
      placeholder: 'Price Range',
      options: [
        { value: '0-100', label: 'Under ₹100' },
        { value: '100-300', label: '₹100 - ₹300' },
        { value: '300-500', label: '₹300 - ₹500' },
        { value: '500+', label: 'Above ₹500' }
      ]
    }
  ];

  const sortOptions = [
    { value: 'name', label: 'Name A-Z' },
    { value: 'price', label: 'Price Low to High' },
    { value: 'price_desc', label: 'Price High to Low' },
    { value: 'rating', label: 'Rating' },
    { value: 'preparation_time', label: 'Preparation Time' }
  ];

  const renderMenuCard = (menu) => (
    <FoodCard
      variant="menu"
      item={menu}
      imageHeight={200}
      onAddToCart={showAddToCart ? () => handleAddToCart(menu) : undefined}
      onView={() => handleViewDetails(menu)}
      onFavorite={showFavorite ? () => handleToggleFavorite(menu) : undefined}
      isFavorite={favorites.has(menu.id)}
      actionsPlacement="footer"
    />
  );

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>{title}</Title>
        <Space wrap>
          <Tag color="blue">{totalItems} items</Tag>
          {categoryId && <Tag color="green">Filtered by category</Tag>}
          {vendorId && <Tag color="orange">Vendor specific</Tag>}
        </Space>
      </div>

      {/* Menu Grid */}
      <MarketplaceGrid
        items={menus}
        renderItem={renderMenuCard}
        loading={loading}
        emptyMessage="No menu items found"
        showPagination={true}
        showFilters={true}
        showSearch={true}
        showSort={true}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        totalItems={totalItems}
        onSearch={handleSearch}
        onFilter={handleFilter}
        onSort={handleSort}
        onPageChange={handlePageChange}
        searchPlaceholder="Search menu items..."
        sortOptions={sortOptions}
        filterOptions={filterOptions}
        gridProps={{
          xs: 24,
          sm: 12,
          md: 8,
          lg: 6,
          xl: 6
        }}
      />

      {/* Floating Action Button (Category Listing) - avoid bottom nav overlap */}
      <FAB
        actions={[{
          label: 'Back to top',
          onClick: () => { try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch(_) {} },
          text: '↑'
        }]}
      />
    </div>
  );
};

export default MenuListing;
